import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SEOHead } from './components/SEO';
import { PerformanceMonitor } from './components/performance';
import { AccessibilityEnhancer } from './components/accessibility';
import ImagePreloader, { CRITICAL_IMAGES } from './components/common/ImagePreloader';
import Header from './components/Header';
import Footer from './components/Footer';
import ThemeProvider from './styles/ThemeProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ConsentBanner from './components/consent/ConsentBanner';
import AuthBootstrap from './components/auth/AuthBootstrap';

const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const ElegantLoginForm = lazy(() => import('./components/auth/ElegantLoginForm'));
const RegisterForm = lazy(() => import('./components/auth/RegisterForm'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordForm = lazy(() => import('./components/auth/ResetPasswordForm'));
const EmailVerification = lazy(() => import('./components/auth/EmailVerification'));
const SupportPage = lazy(() => import('./components/support/SupportPage'));
const VideoTutorialsPage = lazy(() => import('./components/support/VideoTutorialsPage'));
const AgronomicPrinciplesPage = lazy(() => import('./components/support/AgronomicPrinciplesPage'));
const WaterQualityManagementPage = lazy(() => import('./components/support/WaterQualityManagementPage'));
const AdvancedCalculationsPage = lazy(() => import('./components/support/AdvancedCalculationsPage'));
const YieldOptimizationPage = lazy(() => import('./components/support/YieldOptimizationPage'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const AboutPage = lazy(() => import('./pages/public/InfoPages').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./pages/public/InfoPages').then((m) => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('./pages/public/InfoPages').then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/public/InfoPages').then((m) => ({ default: m.TermsPage })));
const EcPpmPage = lazy(() => import('./pages/public/ToolPages').then((m) => ({ default: m.EcPpmPage })));
const StockCalculatorPage = lazy(() => import('./pages/public/ToolPages').then((m) => ({ default: m.StockCalculatorPage })));
const GuidesIndexPage = lazy(() => import('./pages/public/CropGuidePages').then((m) => ({ default: m.GuidesIndexPage })));
const CropGuideRoute = lazy(() => import('./pages/public/CropGuidePages').then((m) => ({ default: m.CropGuideRoute })));
const AbStockGuidePage = lazy(() => import('./pages/public/StockGuidePages').then((m) => ({ default: m.AbStockGuidePage })));
const HydroBuddyComparePage = lazy(() => import('./pages/public/StockGuidePages').then((m) => ({ default: m.HydroBuddyComparePage })));
const GlossaryPage = lazy(() => import('./pages/public/GlossaryPage'));
import { arabicIsLive } from './seo/locale';
const ARABIC_LIVE = arabicIsLive();
const ArabicHomePage = lazy(() => import('./pages/ar/ArabicPages').then((m) => ({ default: m.ArabicHomePage })));
const ArabicPricingPage = lazy(() => import('./pages/ar/ArabicPages').then((m) => ({ default: m.ArabicPricingPage })));
const ArabicAboutPage = lazy(() => import('./pages/ar/ArabicPages').then((m) => ({ default: m.ArabicAboutPage })));
const ArabicContactPage = lazy(() => import('./pages/ar/ArabicPages').then((m) => ({ default: m.ArabicContactPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const UpgradePage = lazy(() => import('./pages/UpgradePage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const VerifyReportPage = lazy(() => import('./pages/VerifyReportPage'));
const MixGuidePage = lazy(() => import('./pages/MixGuidePage'));
const HistoryAnalysisPage = lazy(() => import('./pages/HistoryAnalysisPage'));
const FormulationTuningPage = lazy(() => import('./pages/FormulationTuningPage'));
const ReportPage = lazy(() => import('./pages/ReportPage'));
const WaterQualityPage = lazy(() => import('./pages/WaterQualityPage'));
const UserProfile = lazy(() => import('./components/auth/UserProfile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SaltLibraryWorkflowPage = lazy(() => import('./pages/SaltLibraryWorkflowPage'));
const FormulationWorkflowPage = lazy(() => import('./pages/FormulationWorkflowPage'));
const FormulationPage = lazy(() => import('./views/FormulationPage'));
const StockSolutionsWorkflowPage = lazy(() => import('./pages/StockSolutionsWorkflowPage'));
const UnifiedCalculatorPage = lazy(() => import('./pages/UnifiedCalculatorPage'));
const Results = lazy(() => import('./views/Results'));
const WorkingSolutionPage = lazy(() => import('./pages/WorkingSolutionPage'));
const NutrientDeficiencyPage = lazy(() => import('./pages/NutrientDeficiencyPage'));
const CropRecommendationPage = lazy(() => import('./pages/CropRecommendationPage'));
const NutrientVisualizationPage = lazy(() => import('./pages/NutrientVisualizationPage'));
const ElementAndSaltManagementPage = lazy(() => import('./pages/ElementAndSaltManagementPage'));

const RouteFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
      minHeight: '40vh',
    }}
  >
    <CircularProgress size={28} />
    <Typography variant="body2" color="text.secondary">
      Loading…
    </Typography>
  </Box>
);

/** DSS shell — no feature banners, no side chrome */
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Header />
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          py: { xs: 1.5, md: 2 },
          width: '100%',
          outline: 'none',
        }}
      >
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Router
        future={{
          v7_relativeSplatPath: true,
        }}
      >
        <AuthBootstrap>
          {/* Single global SEO owner — public + app routes (noIndex on app) */}
          <SEOHead />
          <ConsentBanner />
          <PerformanceMonitor enableReporting={true} enableWebVitals={true} />
          <ImagePreloader images={CRITICAL_IMAGES} priority={true} />

          <AccessibilityEnhancer>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Layout><LandingPage /></Layout>} />
              <Route path="/login" element={<ElegantLoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordForm />} />
              <Route path="/verify-email/:token" element={<EmailVerification />} />
              <Route path="/tools/ec-ppm-converter" element={<Layout><EcPpmPage /></Layout>} />
              <Route path="/tools/stock-calculator" element={<Layout><StockCalculatorPage /></Layout>} />
              <Route path="/guides" element={<Layout><GuidesIndexPage /></Layout>} />
              <Route path="/guides/a-and-b-stock-solutions" element={<Layout><AbStockGuidePage /></Layout>} />
              <Route path="/compare/hydrobuddy-alternative" element={<Layout><HydroBuddyComparePage /></Layout>} />
              <Route path="/guides/:slug" element={<Layout><CropGuideRoute /></Layout>} />
              <Route path="/glossary" element={<Layout><GlossaryPage /></Layout>} />
              {/* Arabic pages: built and tested, held back until "arabic": true in seo/publicPages.json */}
              {ARABIC_LIVE && <Route path="/ar" element={<Layout><ArabicHomePage /></Layout>} />}
              {ARABIC_LIVE && <Route path="/ar/pricing" element={<Layout><ArabicPricingPage /></Layout>} />}
              {ARABIC_LIVE && <Route path="/ar/about" element={<Layout><ArabicAboutPage /></Layout>} />}
              {ARABIC_LIVE && <Route path="/ar/contact" element={<Layout><ArabicContactPage /></Layout>} />}
              <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
              <Route path="/about" element={<Layout><AboutPage /></Layout>} />
              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
              <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
              <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
              <Route path="/support" element={<Layout><SupportPage /></Layout>} />
              <Route path="/support/video-tutorials" element={<Layout><VideoTutorialsPage /></Layout>} />
              <Route path="/support/agronomic-principles" element={<Layout><AgronomicPrinciplesPage /></Layout>} />
              <Route path="/support/water-quality-management" element={<Layout><WaterQualityManagementPage /></Layout>} />
              <Route path="/support/advanced-calculations" element={<Layout><AdvancedCalculationsPage /></Layout>} />
              <Route path="/support/yield-optimization" element={<Layout><YieldOptimizationPage /></Layout>} />

              {/* Public: the page behind the QR code on a report */}
              <Route path="/verify/:number" element={<Layout><VerifyReportPage /></Layout>} />

              {/* Canonical redirects (DSS route hygiene) */}
              <Route path="/water-profile" element={<Navigate to="/water-quality" replace />} />
              <Route path="/stock-solution" element={<Navigate to="/stock-solutions" replace />} />

              {/* Protected — primary DSS pipeline */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProjectsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/upgrade"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UpgradePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <HistoryPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mix/:number"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MixGuidePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReportsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/:number"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReportPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis/formulation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FormulationTuningPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis/formulation/:formulationId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FormulationTuningPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis/history"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <HistoryAnalysisPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/runs/:runId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Results />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProjectDetailPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/water-quality"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WaterQualityPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salt-library"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SaltLibraryWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formulation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FormulationWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formulations"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FormulationPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-solutions"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StockSolutionsWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Legacy Stage 5 URL → Results (or Recipe if empty) */}
              <Route
                path="/calculation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UnifiedCalculatorPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/results"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Results />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Secondary tools — kept, not in primary nav */}
              <Route
                path="/working-solution"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WorkingSolutionPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nutrient-deficiency"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <NutrientDeficiencyPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crop-recommendation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CropRecommendationPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nutrient-visualization"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <NutrientVisualizationPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/element-management"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ElementAndSaltManagementPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Retired demo surfaces */}
              <Route path="/ui-demo" element={<Navigate to="/dashboard" replace />} />
              <Route path="/logo-test" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </Suspense>
          </AccessibilityEnhancer>
        </AuthBootstrap>
      </Router>
    </ThemeProvider>
  );
};

export default App;
